import { useState, useEffect, useMemo, useRef, RefCallback } from "react";
import { transformImageUrl, getAlternateBucketUrl, getBucketFromUrl, togglePublicPrefix } from "@/utils/image.util";
import { DEFAULT_PROFILE_IMAGE } from "@/constants/common";
import { ImageValidationResult, ImageChainMetadata } from "./useHeroImage";

interface UseAvatarImageProps {
  largeAvatar?: string;
  smallAvatar?: string;
  avatarUrl?: string;
  profileImageUrl?: string;
  defaultImage?: string; // Override fallback when no image found (e.g. DEFAULT_COVER_IMAGE for card layouts)
  lazy?: boolean;
  trackChain?: boolean; // If true, track validation chain results
}

/**
 * Custom hook for handling avatar image loading with comprehensive fallback chain
 * Tries images in priority order: large -> small -> avatar_url -> profile_image_url -> default
 */
export const useAvatarImage = ({
  largeAvatar = "",
  smallAvatar = "",
  avatarUrl = "",
  profileImageUrl = "",
  defaultImage = DEFAULT_PROFILE_IMAGE,
  lazy = false,
  trackChain = false,
}: UseAvatarImageProps): 
  | string 
  | { resolvedAvatarImage: string; containerRef: RefCallback<HTMLElement> }
  | { resolvedAvatarImage: string; containerRef: RefCallback<HTMLElement>; chainMetadata: ImageChainMetadata | null }
  | { resolvedAvatarImage: string; chainMetadata: ImageChainMetadata | null } => {
  const [resolvedAvatarImage, setResolvedAvatarImage] = useState<string>(defaultImage);
  const [isVisible, setIsVisible] = useState<boolean>(!lazy);
  const [chainMetadata, setChainMetadata] = useState<ImageChainMetadata | null>(null);
  const elementRef = useRef<HTMLElement | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  // Normalize each URL (handle legacy S3 buckets)
  const normalizedLargeAvatar = useMemo(() => {
    if (!largeAvatar) return "";
    const transformed = transformImageUrl(largeAvatar);
    return typeof transformed === "string" ? transformed : largeAvatar;
  }, [largeAvatar]);

  const normalizedSmallAvatar = useMemo(() => {
    if (!smallAvatar) return "";
    const transformed = transformImageUrl(smallAvatar);
    return typeof transformed === "string" ? transformed : smallAvatar;
  }, [smallAvatar]);

  const normalizedAvatarUrl = useMemo(() => {
    if (!avatarUrl) return "";
    const transformed = transformImageUrl(avatarUrl);
    return typeof transformed === "string" ? transformed : avatarUrl;
  }, [avatarUrl]);

  const normalizedProfileImageUrl = useMemo(() => {
    if (!profileImageUrl) return "";
    const transformed = transformImageUrl(profileImageUrl);
    return typeof transformed === "string" ? transformed : profileImageUrl;
  }, [profileImageUrl]);

  // Ref callback to attach to container element
  const containerRef: RefCallback<HTMLElement> = (element) => {
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }

    elementRef.current = element;

    if (lazy && element && typeof window !== 'undefined' && 'IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setIsVisible(true);
              observer.disconnect();
              observerRef.current = null;
            }
          });
        },
        {
          rootMargin: '50px',
          threshold: 0.01,
        }
      );

      observer.observe(element);
      observerRef.current = observer;
    } else if (!lazy) {
      setIsVisible(true);
    }
  };

  // Cleanup observer on unmount
  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
    };
  }, []);

  // Consolidated fallback chain: large -> small -> avatar_url -> profile_image_url -> default
  useEffect(() => {
    if (!isVisible) return;
    let isCancelled = false;

    const chain: ImageValidationResult[] = [];
    const urls = [
      { url: normalizedLargeAvatar, label: "Large", originalUrl: largeAvatar },
      { url: normalizedSmallAvatar, label: "Small", originalUrl: smallAvatar },
      { url: normalizedAvatarUrl, label: "Avatar URL", originalUrl: avatarUrl },
      { url: normalizedProfileImageUrl, label: "Profile Image", originalUrl: profileImageUrl },
    ].filter(item => item.url);

    if (urls.length === 0) {
      const finalDefault = defaultImage;
      setResolvedAvatarImage(finalDefault);
      if (trackChain) {
        setChainMetadata({
          chain: [{ url: finalDefault, label: "Default", success: true }],
          finalImage: finalDefault,
          finalSource: "default"
        });
      }
      return;
    }

    const tryImages = async () => {
      for (const { url, label, originalUrl } of urls) {
        // Helper function to try loading an image URL
        const tryLoadImage = async (imageUrl: string): Promise<boolean> => {
          return new Promise<boolean>((resolve) => {
            const img = new window.Image();
            let timeoutId: number | null = null;
            let isResolved = false;
            
            const handleLoad = () => {
              if (isResolved) return;
              isResolved = true;
              cleanup();
              resolve(true);
            };
            
            const handleError = () => {
              if (isResolved) return;
              isResolved = true;
              cleanup();
              resolve(false);
            };
            
            const cleanup = () => {
              if (timeoutId) {
                clearTimeout(timeoutId);
                timeoutId = null;
              }
              img.onload = null;
              img.onerror = null;
              img.src = '';
            };
            
            img.onload = handleLoad;
            img.onerror = handleError;
            
            // 10 second timeout to prevent hanging
            timeoutId = window.setTimeout(() => {
              if (isResolved) return;
              isResolved = true;
              cleanup();
              resolve(false);
            }, 10000);
            
            img.src = imageUrl;
          });
        };

        // Try primary URL first
        let loaded = await tryLoadImage(url);
        let finalUrl = url;
        let finalLabel = label;
        
        // If primary URL failed and it's an S3 URL, try path variations and alternate bucket
        if (!loaded && url.includes('.s3.') && url.includes('.amazonaws.com')) {
          // Step 1: Try toggling public/ prefix (prod has both old and new structure)
          const hasPublicPrefix = url.includes('/public/');
          const toggledPathUrl = togglePublicPrefix(url, !hasPublicPrefix);
          
          if (toggledPathUrl !== url) {
            loaded = await tryLoadImage(toggledPathUrl);
            if (loaded) {
              finalUrl = toggledPathUrl;
              finalLabel = `${label} (${hasPublicPrefix ? 'no public/' : 'public/'} prefix)`;
            }
          }
          
          // Step 2: If still failed, try alternate bucket with original path
          if (!loaded) {
            const currentBucket = getBucketFromUrl(url);
            const alternateBucket = currentBucket === 'dev' ? 'prod' : currentBucket === 'prod' ? 'dev' : null;
            const alternateUrl = alternateBucket ? getAlternateBucketUrl(url, alternateBucket) : '';
            
            if (alternateUrl) {
              loaded = await tryLoadImage(alternateUrl);
              if (loaded) {
                finalUrl = alternateUrl;
                finalLabel = `${label} (${alternateBucket} bucket)`;
              }
            }
          }
          
          // Step 3: If still failed, try alternate bucket with toggled path
          if (!loaded && toggledPathUrl !== url) {
            const currentBucket = getBucketFromUrl(toggledPathUrl);
            const alternateBucket = currentBucket === 'dev' ? 'prod' : currentBucket === 'prod' ? 'dev' : null;
            const alternateToggledUrl = alternateBucket ? getAlternateBucketUrl(toggledPathUrl, alternateBucket) : '';
            
            if (alternateToggledUrl) {
              loaded = await tryLoadImage(alternateToggledUrl);
              if (loaded) {
                finalUrl = alternateToggledUrl;
                finalLabel = `${label} (${alternateBucket} bucket, ${hasPublicPrefix ? 'no public/' : 'public/'} prefix)`;
              }
            }
          }
          
          // If any variation worked, update the result
          if (loaded) {
            chain.push({
              url,
              label,
              success: false,
              originalUrl: originalUrl || undefined,
            });
            const result: ImageValidationResult = {
              url: finalUrl,
              label: finalLabel,
              success: true,
              originalUrl: originalUrl || undefined,
            };
            chain.push(result);
            
            setResolvedAvatarImage(finalUrl);
            if (trackChain) {
              setChainMetadata({
                chain,
                finalImage: finalUrl,
                finalSource: finalLabel.toLowerCase().replace(/\s+/g, '-')
              });
            }
            return;
          }
        }

        if (isCancelled) return;

        const result: ImageValidationResult = {
          url,
          label,
          success: loaded,
          originalUrl: originalUrl || undefined,
        };
        chain.push(result);

        if (loaded) {
          setResolvedAvatarImage(url);
          if (trackChain) {
            setChainMetadata({
              chain,
              finalImage: url,
              finalSource: label.toLowerCase().replace(/\s+/g, '-')
            });
          }
          return;
        }
      }

      if (!isCancelled) {
        const finalDefault = defaultImage;
        setResolvedAvatarImage(finalDefault);
        chain.push({
          url: finalDefault,
          label: "Default",
          success: true,
        });
        if (trackChain) {
          setChainMetadata({
            chain,
            finalImage: finalDefault,
            finalSource: "default"
          });
        }
      }
    };

    tryImages();

    return () => {
      isCancelled = true;
    };
  }, [
    normalizedLargeAvatar,
    normalizedSmallAvatar,
    normalizedAvatarUrl,
    normalizedProfileImageUrl,
    largeAvatar,
    smallAvatar,
    avatarUrl,
    profileImageUrl,
    defaultImage,
    isVisible,
    trackChain,
  ]);

  // Return based on what's requested
  if (lazy && trackChain) {
    return { resolvedAvatarImage, containerRef, chainMetadata: chainMetadata || null };
  }
  if (lazy) {
    return { resolvedAvatarImage, containerRef };
  }
  if (trackChain) {
    return { resolvedAvatarImage, chainMetadata: chainMetadata || null };
  }
  return resolvedAvatarImage;
};
